import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { ArrowLeft, Paperclip, Download, ExternalLink, Link as LinkIcon, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { mediaUrl, fmtDay, TYPE_LABEL, ytId } from "./agentParts";

function Video({ url }) {
  const yt = ytId(url);
  const vimeo = (url || "").match(/vimeo\.com\/(\d+)/)?.[1];
  const loom = (url || "").match(/loom\.com\/share\/(\w+)/)?.[1];
  const src = yt ? `https://www.youtube.com/embed/${yt}` : vimeo ? `https://player.vimeo.com/video/${vimeo}` : loom ? `https://www.loom.com/embed/${loom}` : null;
  return (
    <div className="aspect-video rounded-2xl overflow-hidden bg-black" data-testid="content-video">
      {src ? <iframe src={src} title="Training video" className="w-full h-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
        : <video src={mediaUrl(url)} controls className="w-full h-full" />}
    </div>
  );
}

export default function ContentReader() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const preview = sp.get("preview") === "agent";
  const navigate = useNavigate();
  const fromDash = useLocation().state?.from === "dashboard";
  const [item, setItem] = useState(undefined);
  useEffect(() => {
    api.get(`/content/items/${id}${preview ? "?preview=agent" : ""}`).then((r) => setItem(r.data)).catch(() => setItem(null));
  }, [id, preview]);
  if (item === undefined) return <div className="max-w-3xl mx-auto h-96 rounded-2xl bg-slate-100 animate-pulse" />;
  if (item === null) return <p className="text-center text-slate-500 py-20" data-testid="content-not-found">This item isn't available to you.</p>;
  return (
    <article className="max-w-3xl mx-auto tr-rise" data-testid="content-reader">
      <button onClick={() => (fromDash ? navigate(preview ? "/agent-dashboard" : "/") : navigate(-1))} className="text-sm text-slate-500 hover:text-brand inline-flex items-center gap-1 mb-6" data-testid="content-back"><ArrowLeft className="w-4 h-4" />{fromDash ? "Back to Agent Dashboard" : "Back"}</button>
      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand/80">{item.category || TYPE_LABEL[item.type]}</p>
      <h1 className="font-editorial text-3xl sm:text-4xl text-slate-900 mt-2 leading-tight" data-testid="content-title">{item.title}</h1>
      <p className="mt-3 text-sm text-slate-500" data-testid="content-meta">{item.reviewed_at ? `Last reviewed ${fmtDay(item.reviewed_at)}` : [item.author_name, item.author_title, fmtDay(item.published_on), item.duration_min ? `${item.duration_min} min` : null].filter(Boolean).join(" · ")}</p>
      {item.collection === "grow" && <p className="mt-1 text-xs text-slate-400" data-testid="content-internal-note">For authorized Agent and agency use only. Program rules, eligibility and availability may vary — current Farmers guidance controls.</p>}
      {item.video_url ? <div className="mt-6"><Video url={item.video_url} /></div>
        : item.image_url ? <img src={mediaUrl(item.image_url)} alt="" className="mt-6 w-full max-h-[420px] object-cover rounded-2xl" /> : null}
      {item.summary && <p className="mt-6 text-lg text-slate-700 leading-relaxed">{item.summary}</p>}
      {item.body_html && <div className="tr-prose mt-5 text-[16px] leading-[1.75] text-slate-800" data-testid="content-body" dangerouslySetInnerHTML={{ __html: item.body_html }} />}
      {item.related?.length > 0 && (
        <div className="mt-10 border-t border-slate-200 pt-6" data-testid="content-related">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Related resources</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {item.related.map((r) => (
              <button key={r.id} onClick={() => navigate(`/library/${r.id}${preview ? "?preview=agent" : ""}`, { state: { from: fromDash ? "dashboard" : undefined } })} data-testid={`content-related-${r.id}`}
                className="group text-left rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-brand/40 transition-colors">
                <span className="block text-[11px] uppercase tracking-wider font-semibold text-brand/80">{r.category}</span>
                <span className="block font-medium text-slate-900 group-hover:text-brand leading-snug">{r.title}</span>
                <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-brand">Read Article <ArrowRight className="w-3 h-3" /></span>
              </button>
            ))}
          </div>
        </div>
      )}
      {item.cta_url && <a href={item.cta_url} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-brand text-white text-sm font-semibold px-5 py-2.5 hover:bg-brand-strong" data-testid="content-cta">{item.cta_label || "Learn more"} <ExternalLink className="w-4 h-4" /></a>}
      {(item.attachments?.length > 0 || item.links?.length > 0) && (
        <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50/60 p-5" data-testid="content-attachments">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Files & links</p>
          <div className="space-y-2">
            {item.attachments?.map((a, i) => (
              <a key={i} href={`${mediaUrl(a.url)}?download=true`} target="_blank" rel="noreferrer" data-testid={`content-attachment-${i}`}
                className="flex items-center gap-3 rounded-lg bg-white border border-slate-200 px-3 py-2.5 hover:border-brand/40">
                <Paperclip className="w-4 h-4 text-slate-400" /><span className="flex-1 text-sm font-medium text-slate-800 truncate">{a.name}</span><Download className="w-4 h-4 text-slate-400" />
              </a>
            ))}
            {item.links?.map((l, i) => (
              <a key={`l${i}`} href={l.url} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-lg bg-white border border-slate-200 px-3 py-2.5 hover:border-brand/40" data-testid={`content-link-${i}`}>
                <LinkIcon className="w-4 h-4 text-slate-400" /><span className="flex-1 text-sm font-medium text-slate-800 truncate">{l.label || l.url}</span><ExternalLink className="w-4 h-4 text-slate-400" />
              </a>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
